import React, { useState, useRef, useEffect } from 'react';
import { User, ShoppingCart, ChevronDown, ArrowRight, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

const LOGO_URL = "/images/logo.webp";

// Toro Pharma products with slugs
const toroPharmaProducts = [
  { name: 'BPC-157', slug: 'bpc-157' },
  { name: 'GHK-Cu', slug: 'ghk-cu' },
  { name: 'NAD+', slug: 'nad-plus' },
  { name: 'TB500', slug: 'tb500' },
  { name: 'MOTS-C', slug: 'mots-c' },
  { name: 'AOD-9604', slug: 'aod-9604' },
  { name: 'BIOGLUTIDE', slug: 'bioglutide' },
  { name: 'DSIP', slug: 'dsip' },
  { name: 'KISSPEPTIN', slug: 'kisspeptin' },
  { name: 'LL-37', slug: 'll-37' },
  { name: 'SELANK', slug: 'selank' },
  { name: 'SEMAX', slug: 'semax' },
  { name: 'SLU-PP-332', slug: 'slu-pp-332' },
  { name: 'SS-31', slug: 'ss-31' },
  { name: 'VILON', slug: 'vilon' },
  { name: 'VIP', slug: 'vip' },
  { name: 'RETATRUTIDE', slug: 'retatrutide' },
  { name: 'Bacteriostatic Water', slug: 'bac-water' },
];

const supplementDropdown = {
  columns: [
    { name: "BPC 157 + TB500", slug: "bpc157-tb500-tablets" },
    { name: "GNC Fish Oil 1000 mg", slug: "gnc-fish-oil" },
    { name: "Magnesium Glycinate", slug: "magnesium-glycinate" },
  ]
};

const dietFuelProducts = [
  { name: "Diet Fuel Cocoa", slug: "diet-fuel-cocoa", image: "/images/diet-fuel-cocoa/grid.webp" },
  { name: "Diet Fuel Coconut Macadamia", slug: "diet-fuel-coconut", image: "/images/diet-fuel-coconut/grid.webp" },
];

const beautyDropdown = {
  columns: [
    { heading: "Skin Protocols", items: [
      { name: "REVIGEN", slug: "revigen" },
      { name: "GLOW", slug: "glow" },
      { name: "GHK-Cu", slug: "ghk-cu" },
    ]},
  ]
};

export const ValuePropositionBanner = () => {
  return (
    <a 
      href="https://lifereversal.in"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="value-proposition-banner"
      className="block w-full py-1.5 text-center transition-all duration-200 hover:brightness-110 cursor-pointer"
      style={{ backgroundColor: '#1A3D36' }}
    >
      <span 
        className="text-sm font-sans font-medium inline-flex items-center gap-1.5"
        style={{ color: '#FFFFFF', letterSpacing: '0.01em' }}
      >
        Begin Your Protocol
        <ArrowRight size={13} strokeWidth={2} />
      </span>
    </a>
  );
};

export const Header = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState(null);
  const closeTimer = useRef(null);
  const navigate = useNavigate();

  let cart = { getCartCount: () => 0, setIsCartOpen: () => {} };
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    cart = useCart();
  } catch (e) {}

  const openDropdown = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(key);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 120);
  };

  useEffect(() => {
    return () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  }, []);

  const goTo = (path) => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
    setMobileAccordion(null);
    navigate(path);
  };

  const toggleMobileAccordion = (key) => {
    setMobileAccordion(prev => prev === key ? null : key);
  };

  return (
    <header 
      data-testid="header"
      className="w-full relative"
      style={{ backgroundColor: '#F5F3EE', zIndex: 50 }}
    >
      {/* Desktop Nav */}
      <div className="flex items-center justify-between px-8 py-2.5 border-b" style={{ borderColor: '#D9D6CF' }}>
        {/* Logo */}
        <a href="/" data-testid="logo" className="flex items-center gap-2.5" onClick={(e) => { e.preventDefault(); goTo('/'); }}>
          <img src={LOGO_URL} alt="Life Reversal" className="h-8 w-auto" />
          <span className="text-lg font-serif font-semibold" style={{ color: '#2B2B2B', letterSpacing: '0.02em' }}>
            Life Reversal
          </span>
        </a>

        {/* Desktop Center Navigation */}
        <nav className="hidden lg:flex items-center gap-7">
          {[
            { key: 'peptide', label: 'Peptide' },
            { key: 'supplement', label: 'Supplement' },
            { key: 'dietfuel', label: 'Diet Fuel' },
            { key: 'beauty', label: 'Beauty' },
          ].map(item => (
            <div 
              key={item.key}
              className="relative"
              onMouseEnter={() => openDropdown(item.key)}
              onMouseLeave={scheduleClose}
            >
              <button 
                data-testid={`nav-${item.key}`}
                className="flex items-center gap-1 text-sm font-sans font-medium transition-colors duration-200 py-2"
                style={{ color: activeDropdown === item.key ? '#1A3D36' : '#2B2B2B', cursor: 'pointer' }}
              >
                <span>{item.label}</span>
                <ChevronDown size={13} className={`transition-transform duration-200 ${activeDropdown === item.key ? 'rotate-180' : ''}`} />
              </button>
            </div>
          ))}
        </nav>

        {/* Right Icons */}
        <div className="flex items-center gap-4">
          {/* Mobile menu toggle */}
          <button 
            data-testid="mobile-menu-toggle"
            className="p-1 lg:hidden"
            style={{ color: '#2B2B2B' }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
          <button 
            data-testid="account-icon"
            className="p-1 hover:opacity-70 transition-opacity hidden lg:block"
            style={{ color: '#2B2B2B' }}
          >
            <User size={19} strokeWidth={1.5} />
          </button>
          <button 
            data-testid="cart-icon"
            className="p-1 hover:opacity-70 transition-opacity relative"
            style={{ color: '#2B2B2B' }}
            onClick={() => cart.setIsCartOpen(true)}
          >
            <ShoppingCart size={19} strokeWidth={1.5} />
            {cart.getCartCount() > 0 && (
              <span 
                className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center text-[10px] font-medium rounded-full"
                style={{ backgroundColor: '#1A3D36', color: '#FFFFFF' }}
              >
                {cart.getCartCount()}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ===== DESKTOP MEGA DROPDOWNS ===== */}

      {/* Peptide Dropdown */}
      <div 
        data-testid="peptide-dropdown"
        className={`absolute left-0 right-0 transition-all duration-200 hidden lg:block ${
          activeDropdown === 'peptide' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1 pointer-events-none'
        }`}
        style={{ 
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E8E6E1',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
          zIndex: 50,
        }}
        onMouseEnter={() => openDropdown('peptide')}
        onMouseLeave={scheduleClose}
      >
        <div className="max-w-4xl mx-auto px-12 py-8">
          <div className="grid grid-cols-3 gap-16">
            {/* Toro Pharma — clickable links */}
            <div>
              <h3 
                className="text-[11px] font-sans font-bold uppercase mb-4 pb-2"
                style={{ color: '#2B2B2B', letterSpacing: '0.12em', borderBottom: '1px solid #E8E6E1' }}
              >
                Toro Pharma
              </h3>
              <ul className="space-y-2">
                {toroPharmaProducts.map((item) => (
                  <li key={item.slug}>
                    <button 
                      onClick={() => goTo(`/product/${item.slug}`)}
                      className="text-sm font-sans transition-colors duration-150 block w-full text-left py-0.5"
                      style={{ color: '#6B6B6B', cursor: 'pointer' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#1A3D36'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#6B6B6B'; }}
                      data-testid={`peptide-link-${item.slug}`}
                    >
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Biomolecule — Coming Soon */}
            <div>
              <h3 
                className="text-[11px] font-sans font-bold uppercase mb-4 pb-2"
                style={{ color: '#2B2B2B', letterSpacing: '0.12em', borderBottom: '1px solid #E8E6E1' }}
              >
                Biomolecule
              </h3>
              <p 
                className="text-sm italic mt-6"
                style={{ color: '#A8A8A8' }}
                data-testid="biomolecule-coming-soon"
              >
                Coming Soon
              </p>
            </div>

            {/* Denik — Coming Soon */}
            <div>
              <h3 
                className="text-[11px] font-sans font-bold uppercase mb-4 pb-2"
                style={{ color: '#2B2B2B', letterSpacing: '0.12em', borderBottom: '1px solid #E8E6E1' }}
              >
                Denik
              </h3>
              <p 
                className="text-sm italic mt-6"
                style={{ color: '#A8A8A8' }}
                data-testid="denik-coming-soon"
              >
                Coming Soon
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Supplement Dropdown */}
      <div 
        data-testid="supplement-dropdown"
        className={`absolute left-0 right-0 transition-all duration-200 hidden lg:block ${
          activeDropdown === 'supplement' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1 pointer-events-none'
        }`}
        style={{ 
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E8E6E1',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
          zIndex: 50,
        }}
        onMouseEnter={() => openDropdown('supplement')}
        onMouseLeave={scheduleClose}
      >
        <div className="max-w-3xl mx-auto px-12 py-6">
          <div className="flex flex-wrap justify-center gap-8">
            {supplementDropdown.columns.map((col) => (
              <button
                key={col.slug}
                onClick={() => goTo(`/product/${col.slug}`)}
                className="text-sm font-sans font-medium transition-colors duration-150 py-1 px-2"
                style={{ color: '#2B2B2B', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#1A3D36'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#2B2B2B'; }}
                data-testid={`supplement-link-${col.slug}`}
              >
                {col.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Diet Fuel Dropdown */}
      <div 
        data-testid="dietfuel-dropdown"
        className={`absolute left-0 right-0 transition-all duration-200 hidden lg:block ${
          activeDropdown === 'dietfuel' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1 pointer-events-none'
        }`}
        style={{ 
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E8E6E1',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
          zIndex: 50,
        }}
        onMouseEnter={() => openDropdown('dietfuel')}
        onMouseLeave={scheduleClose}
      >
        <div className="max-w-2xl mx-auto px-12 py-6">
          <div className="flex items-center justify-center gap-10">
            {dietFuelProducts.map((product) => (
              <button 
                key={product.slug}
                onClick={() => goTo(`/product/${product.slug}`)}
                className="text-center group cursor-pointer"
                data-testid={`dietfuel-link-${product.slug}`}
              >
                <div className="w-56 h-72 mb-2 overflow-hidden flex items-center justify-center">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <p className="text-sm font-sans font-medium transition-colors duration-200 group-hover:text-[#1A3D36]" style={{ color: '#2B2B2B' }}>
                  {product.name}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Beauty Dropdown */}
      <div 
        data-testid="beauty-dropdown"
        className={`absolute left-0 right-0 transition-all duration-200 hidden lg:block ${
          activeDropdown === 'beauty' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1 pointer-events-none'
        }`}
        style={{ 
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E8E6E1',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
          zIndex: 50,
        }}
        onMouseEnter={() => openDropdown('beauty')}
        onMouseLeave={scheduleClose}
      >
        <div className="max-w-3xl mx-auto px-12 py-6">
          {beautyDropdown.columns.map((column, idx) => (
            <div key={idx}>
              <h3 
                className="text-[11px] font-sans font-bold uppercase mb-4 pb-2"
                style={{ color: '#2B2B2B', letterSpacing: '0.12em', borderBottom: '1px solid #E8E6E1' }}
              >
                {column.heading}
              </h3>
              <div className="flex flex-wrap gap-6">
                {column.items.map((item) => (
                  <button
                    key={item.slug}
                    onClick={() => goTo(`/product/${item.slug}`)}
                    className="text-sm font-sans transition-colors duration-150 py-0.5"
                    style={{ color: '#6B6B6B', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#1A3D36'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#6B6B6B'; }}
                    data-testid={`beauty-link-${item.slug}`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== MOBILE MENU ===== */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden absolute left-0 right-0 bg-white overflow-y-auto"
          style={{ 
            borderTop: '1px solid #E8E6E1', 
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
            zIndex: 50,
            maxHeight: 'calc(100vh - 60px)',
          }}
          data-testid="mobile-menu"
        >
          {/* Peptide Accordion */}
          <div style={{ borderBottom: '1px solid #F0EEE9' }}>
            <button 
              onClick={() => toggleMobileAccordion('peptide')}
              className="w-full flex items-center justify-between px-6 py-4"
              data-testid="mobile-nav-peptide"
            >
              <span className="text-sm font-medium" style={{ color: '#2B2B2B' }}>Peptide</span>
              <ChevronDown size={15} style={{ color: '#6B6B6B', transform: mobileAccordion === 'peptide' ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </button>
            {mobileAccordion === 'peptide' && (
              <div className="px-6 pb-4">
                {/* Toro Pharma */}
                <p className="text-[10px] font-bold uppercase mb-2" style={{ color: '#2B2B2B', letterSpacing: '0.1em' }}>Toro Pharma</p>
                <div className="space-y-1 mb-4">
                  {toroPharmaProducts.map((item) => (
                    <button
                      key={item.slug}
                      onClick={() => goTo(`/product/${item.slug}`)}
                      className="block text-sm py-1 w-full text-left"
                      style={{ color: '#6B6B6B' }}
                      data-testid={`mobile-peptide-${item.slug}`}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
                {/* Biomolecule */}
                <p className="text-[10px] font-bold uppercase mb-1.5" style={{ color: '#2B2B2B', letterSpacing: '0.1em' }}>Biomolecule</p>
                <p className="text-sm italic mb-4" style={{ color: '#A8A8A8' }}>Coming Soon</p>
                {/* Denik */}
                <p className="text-[10px] font-bold uppercase mb-1.5" style={{ color: '#2B2B2B', letterSpacing: '0.1em' }}>Denik</p>
                <p className="text-sm italic" style={{ color: '#A8A8A8' }}>Coming Soon</p>
              </div>
            )}
          </div>

          {/* Supplement Accordion */}
          <div style={{ borderBottom: '1px solid #F0EEE9' }}>
            <button 
              onClick={() => toggleMobileAccordion('supplement')}
              className="w-full flex items-center justify-between px-6 py-4"
              data-testid="mobile-nav-supplement"
            >
              <span className="text-sm font-medium" style={{ color: '#2B2B2B' }}>Supplement</span>
              <ChevronDown size={15} style={{ color: '#6B6B6B', transform: mobileAccordion === 'supplement' ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </button>
            {mobileAccordion === 'supplement' && (
              <div className="px-6 pb-4 space-y-1">
                {supplementDropdown.columns.map((col) => (
                  <button
                    key={col.slug}
                    onClick={() => goTo(`/product/${col.slug}`)}
                    className="block text-sm py-1 w-full text-left"
                    style={{ color: '#6B6B6B' }}
                  >
                    {col.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Diet Fuel Accordion */}
          <div style={{ borderBottom: '1px solid #F0EEE9' }}>
            <button 
              onClick={() => toggleMobileAccordion('dietfuel')}
              className="w-full flex items-center justify-between px-6 py-4"
              data-testid="mobile-nav-dietfuel"
            >
              <span className="text-sm font-medium" style={{ color: '#2B2B2B' }}>Diet Fuel</span>
              <ChevronDown size={15} style={{ color: '#6B6B6B', transform: mobileAccordion === 'dietfuel' ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </button>
            {mobileAccordion === 'dietfuel' && (
              <div className="px-6 pb-4 space-y-1">
                {dietFuelProducts.map((p) => (
                  <button
                    key={p.slug}
                    onClick={() => goTo(`/product/${p.slug}`)}
                    className="block text-sm py-1 w-full text-left"
                    style={{ color: '#6B6B6B' }}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Beauty Accordion */}
          <div style={{ borderBottom: '1px solid #F0EEE9' }}>
            <button 
              onClick={() => toggleMobileAccordion('beauty')}
              className="w-full flex items-center justify-between px-6 py-4"
              data-testid="mobile-nav-beauty"
            >
              <span className="text-sm font-medium" style={{ color: '#2B2B2B' }}>Beauty</span>
              <ChevronDown size={15} style={{ color: '#6B6B6B', transform: mobileAccordion === 'beauty' ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </button>
            {mobileAccordion === 'beauty' && (
              <div className="px-6 pb-4 space-y-1">
                {beautyDropdown.columns.map((col) => col.items.map((item) => (
                  <button
                    key={item.slug}
                    onClick={() => goTo(`/product/${item.slug}`)}
                    className="block text-sm py-1 w-full text-left"
                    style={{ color: '#6B6B6B' }}
                  >
                    {item.name}
                  </button>
                )))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
